import { createContext, useContext, useEffect, useReducer, useState } from 'react';

import type { ChangeEventHandler } from 'react';
import type React from 'react';

type State<OptionType> = {
  searchString?: string;
  selected: OptionType | null;
  options: OptionType[];
  hasNoResults: boolean;
  hasPagination: boolean;
  isLoading: boolean;
  isSubmitting: boolean;
};

type UseSearch<OptionType> = State<OptionType> & {
  mapOptionToId: (optionType: OptionType) => string;
  selectOption: (option: OptionType) => void;
  continueWithoutSelection: () => void;
  searchCompanies: ChangeEventHandler<HTMLInputElement>;
  showMoreResults: () => void;
  goToNextPage: () => void;
};

export type UseSearchProviderProps<OptionType> = {
  mapOptionToId: (optionType: OptionType) => string;
  children: React.ReactNode;
  selected: OptionType | null;
  options: OptionType[];
  hasPagination?: boolean;
  isSubmitting?: boolean;
  eventHandlers: {
    onSearch: (searchString: string) => void;
    onSelect: (option: OptionType) => void;
    onContinueWithoutValue?: () => void;
    onContinue: () => void;
    onShowMore?: () => void;
  };
};

type Action<OptionType> =
  | {
      type: 'ENTER_SEARCH';
      payload: string;
    }
  | {
      type: 'SHOW_MORE_RESULTS';
    }
  | {
      type: 'PROP_RESULTS_CHANGED';
      payload: {
        results: OptionType[];
        hasPagination: boolean;
      };
    }
  | {
      type: 'PROP_SELECTED_CHANGED';
      payload: OptionType | null;
    };

const reducer = <OptionType,>(state: State<OptionType>, action: Action<OptionType>) => {
  switch (action.type) {
    case 'ENTER_SEARCH': {
      return {
        ...state,
        isLoading: !!action.payload.length,
        searchString: action.payload,
      };
    }
    case 'SHOW_MORE_RESULTS': {
      return {
        ...state,
        isLoading: true,
      };
    }
    case 'PROP_RESULTS_CHANGED': {
      return {
        ...state,
        isLoading: false,
        options: action.payload.results,
        hasPagination: action.payload.hasPagination,
        hasNoResults: !!state.searchString?.length && !action.payload.results.length,
      };
    }
    case 'PROP_SELECTED_CHANGED': {
      return {
        ...state,
        selected: action.payload,
      };
    }
    default: {
      return state;
    }
  }
};

const defaultState = {
  searchString: '',
  hasNoResults: false,
  hasPagination: false,
  isLoading: false,
};

/* eslint-disable @typescript-eslint/no-explicit-any */
const optionSelectContext = createContext<UseSearch<any> | undefined>(undefined);

export const UseSearchProvider = <OptionType,>({
  children,
  eventHandlers,
  mapOptionToId,
  hasPagination: hasPaginationFromProps,
  ...props
}: UseSearchProviderProps<OptionType>) => {
  const isSubmitting = !!props.isSubmitting;
  const [state, dispatch] = useReducer<React.Reducer<State<OptionType>, Action<OptionType>>>(reducer, {
    ...defaultState,
    selected: props.selected,
    options: props.options,
    isSubmitting,
  });
  const [isInitialized, setIsInitialized] = useState(false);

  const selectOption = (option: OptionType) => {
    eventHandlers.onSelect?.(option);
  };

  const continueWithoutSelection = () => {
    eventHandlers.onContinueWithoutValue?.();
  };

  const searchCompanies: ChangeEventHandler<HTMLInputElement> = (e) => {
    dispatch({ type: 'ENTER_SEARCH', payload: e.target.value });
    eventHandlers.onSearch?.(e.target.value);
  };

  const showMoreResults = () => {
    dispatch({ type: 'SHOW_MORE_RESULTS' });
    eventHandlers.onShowMore?.();
  };

  const goToNextPage = () => {
    state.selected && eventHandlers.onContinue?.();
  };

  useEffect(() => {
    isInitialized &&
      dispatch({
        type: 'PROP_RESULTS_CHANGED',
        payload: {
          results: props.options,
          hasPagination: hasPaginationFromProps || false,
        },
      });
  }, [props.options]);

  useEffect(() => {
    isInitialized && dispatch({ type: 'PROP_SELECTED_CHANGED', payload: props.selected });
  }, [props.selected]);

  useEffect(() => {
    setIsInitialized(true);
  }, []);

  return (
    <optionSelectContext.Provider
      value={{
        ...state,
        isSubmitting,
        selectOption,
        continueWithoutSelection,
        searchCompanies,
        showMoreResults,
        goToNextPage,
        mapOptionToId,
      }}
    >
      {children}
    </optionSelectContext.Provider>
  );
};

export const useSearch = <OptionType,>() => {
  const context = useContext(optionSelectContext);
  if (context === undefined) {
    throw new Error('useSearch must be used within a UseSearchProvider');
  }
  return context as UseSearch<OptionType>;
};
